import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, FileText, Download, X, Scissors } from 'lucide-react'
import { PDFDocument } from 'pdf-lib'
import toast from 'react-hot-toast'
import AdContainer from '../components/ads/AdContainer'
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig'
import { useSEO, createToolPageStructuredData, createBreadcrumbStructuredData, generateKeywords } from '../hooks/useSEO'

interface PDFFile {
  id: string
  file: File
  name: string
  pageCount: number
  size: number
  status: 'loaded' | 'error'
}

interface SplitRange {
  id: string
  start: number
  end: number
  name: string
}

interface SplitResult {
  id: string
  name: string
  blob: Blob
  pageCount: number
}

const PDFSplitter: React.FC = () => {
  // SEO for PDF splitter page
  useSEO({
    title: 'PDF Splitter - Split PDF Pages Online Free | Extract PDF Pages | SnapTools',
    description: 'Split PDF files online free. Extract specific pages or split by page ranges. Professional PDF splitter tool. Fast, secure, no registration required. Split large PDF documents instantly.',
    keywords: generateKeywords('pdf', [
      'PDF splitter online free',
      'split PDF pages',
      'extract PDF pages',
      'PDF page extractor',
      'divide PDF file',
      'separate PDF pages',
      'split PDF document',
      'PDF page splitter',
      'extract pages from PDF',
      'PDF divider tool',
      'split PDF online',
      'PDF page separator',
      'break PDF into pages',
      'PDF page extraction',
      'split large PDF',
      'PDF section splitter',
      'PDF range splitter',
      'custom PDF split',
      'PDF chapter splitter',
      'PDF book splitter',
      'split PDF by pages',
      'PDF page breaker',
      'PDF document splitter',
      'free PDF splitter',
      'online PDF splitter',
      'web PDF splitter',
      'browser PDF splitter',
      'secure PDF splitter',
      'private PDF splitter',
      'no upload PDF splitter'
    ]),
    canonical: 'https://snap-tools.online/pdf/split',
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        createToolPageStructuredData(
          'PDF Splitter',
          'Split PDF files and extract specific pages online. Free, fast, and secure PDF splitting tool.',
          'https://snap-tools.online/pdf/split'
        ),
        createBreadcrumbStructuredData([
          { name: 'Home', url: 'https://snap-tools.online/' },
          { name: 'PDF Tools', url: 'https://snap-tools.online/' },
          { name: 'PDF Splitter', url: 'https://snap-tools.online/pdf/split' }
        ])
      ]
    }
  })

  const [pdfFile, setPdfFile] = useState<PDFFile | null>(null)
  const [splitRanges, setSplitRanges] = useState<SplitRange[]>([])
  const [splitResults, setSplitResults] = useState<SplitResult[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [splitMode, setSplitMode] = useState<'pages' | 'ranges'>('ranges')

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const pdfFiles = acceptedFiles.filter(file => 
      file.type === 'application/pdf'
    )

    if (pdfFiles.length === 0) {
      toast.error('Please select a valid PDF file')
      return
    }

    if (pdfFiles.length > 1) {
      toast.error('Please select only one PDF file')
      return
    }

    const file = pdfFiles[0]
    
    try {
      const arrayBuffer = await file.arrayBuffer()
      const pdfDoc = await PDFDocument.load(arrayBuffer)
      const pageCount = pdfDoc.getPageCount()
      
      setPdfFile({
        id: Math.random().toString(36).substr(2, 9),
        file,
        name: file.name,
        pageCount,
        size: file.size,
        status: 'loaded'
      })
      
      // Reset state
      setSplitRanges([])
      setSplitResults([])
      
      toast.success(`PDF loaded with ${pageCount} pages`)
    } catch (error) {
      toast.error('Failed to load PDF file')
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false
  })

  const addSplitRange = () => {
    if (!pdfFile) return
    
    const newRange: SplitRange = {
      id: Math.random().toString(36).substr(2, 9),
      start: 1,
      end: Math.min(10, pdfFile.pageCount),
      name: `Split ${splitRanges.length + 1}`
    }
    
    setSplitRanges(prev => [...prev, newRange])
  }

  const updateSplitRange = (id: string, field: keyof SplitRange, value: string | number) => {
    setSplitRanges(prev => prev.map(range => 
      range.id === id ? { ...range, [field]: value } : range
    ))
  }

  const removeSplitRange = (id: string) => {
    setSplitRanges(prev => prev.filter(range => range.id !== id))
  }

  const splitPDF = async () => {
    if (!pdfFile) {
      toast.error('Please select a PDF file first')
      return
    }

    if (splitMode === 'ranges' && splitRanges.length === 0) {
      toast.error('Please add at least one split range')
      return
    }

    setIsProcessing(true)
    setSplitResults([])
    
    try {
      const arrayBuffer = await pdfFile.file.arrayBuffer()
      const sourcePdf = await PDFDocument.load(arrayBuffer)
      const results: SplitResult[] = []
      
      if (splitMode === 'pages') {
        // Split into individual pages
        for (let i = 0; i < pdfFile.pageCount; i++) {
          const newPdf = await PDFDocument.create()
          const [page] = await newPdf.copyPages(sourcePdf, [i])
          newPdf.addPage(page)
          
          const pdfBytes = await newPdf.save()
          const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' })
          
          results.push({
            id: Math.random().toString(36).substr(2, 9),
            name: `${pdfFile.name.split('.')[0]}_page_${i + 1}.pdf`,
            blob,
            pageCount: 1
          })
        }
      } else {
        // Split by ranges
        for (const range of splitRanges) {
          if (range.start < 1 || range.end > pdfFile.pageCount || range.start > range.end) {
            toast.error(`Invalid range: ${range.start}-${range.end}`)
            continue
          }
          
          const newPdf = await PDFDocument.create()
          const pageIndices = Array.from(
            { length: range.end - range.start + 1 }, 
            (_, i) => range.start - 1 + i
          )
          
          const pages = await newPdf.copyPages(sourcePdf, pageIndices)
          pages.forEach(page => newPdf.addPage(page))
          
          const pdfBytes = await newPdf.save()
          const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' })
          
          results.push({
            id: Math.random().toString(36).substr(2, 9),
            name: `${range.name}.pdf`,
            blob,
            pageCount: range.end - range.start + 1
          })
        }
      }
      
      setSplitResults(results)
      toast.success(`PDF split into ${results.length} files`)
      
    } catch (error) {
      console.error('Error splitting PDF:', error)
      toast.error('Failed to split PDF')
    } finally {
      setIsProcessing(false)
    }
  }

  const downloadFile = (result: SplitResult) => {
    const url = URL.createObjectURL(result.blob)
    const a = document.createElement('a')
    a.href = url
    a.download = result.name
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const downloadAll = () => {
    splitResults.forEach(result => {
      setTimeout(() => downloadFile(result), 100)
    })
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-2xl mb-6">
            <FileText className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#8438ff] mb-4">
            PDF Splitter
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Split PDF files into individual pages or custom page ranges with precise control and lightning speed.
          </p>
        </div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfSplitter.header}
          className="mb-8"
        />

        {/* Upload Area */}
        <div
          {...getRootProps()}
          className={`card border-2 border-dashed cursor-pointer transition-colors mb-8 ${
            isDragActive ? 'border-[#8438ff] bg-[#8438ff]/10' : 'border-gray-300 hover:border-[#8438ff]'
          }`}
        >
          <input {...getInputProps()} />
          <div className="text-center py-12">
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-xl font-medium text-[#8438ff] mb-2">
              {isDragActive ? 'Drop PDF file here' : 'Choose PDF or drag it here'}
            </p>
            <p className="text-gray-600">
              Select a PDF file to split into multiple documents
            </p>
          </div>
        </div>

        {/* Content Ad - appears when PDF is uploaded */}
        {pdfFile && (
          <AdContainer 
            position="content" 
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfSplitter.content}
            className="mb-8"
          />
        )}

        {/* PDF Info */}
        {pdfFile && (
          <div className="card mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#8438ff]">Selected PDF</h3>
              <button
                onClick={() => {
                  setPdfFile(null)
                  setSplitRanges([])
                  setSplitResults([])
                }}
                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            
            <div className="flex items-center space-x-4">
              <FileText className="h-8 w-8 text-gray-400" />
              <div>
                <p className="font-medium text-[#8438ff]">{pdfFile.name}</p>
                <p className="text-sm text-gray-600">
                  {pdfFile.pageCount} pages • {formatFileSize(pdfFile.size)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Split Options */}
        {pdfFile && (
          <div className="card mb-8">
            <h3 className="text-lg font-semibold text-[#8438ff] mb-4">Split Options</h3>
            
            <div className="flex space-x-4 mb-6">
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="splitMode"
                  value="ranges"
                  checked={splitMode === 'ranges'}
                  onChange={(e) => setSplitMode(e.target.value as 'pages' | 'ranges')}
                  className="w-4 h-4 text-[#8438ff] border-gray-300 focus:ring-[#8438ff]"
                />
                <span className="text-sm font-medium text-gray-700">Custom page ranges</span>
              </label>
              
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="splitMode"
                  value="pages"
                  checked={splitMode === 'pages'}
                  onChange={(e) => setSplitMode(e.target.value as 'pages' | 'ranges')}
                  className="w-4 h-4 text-[#8438ff] border-gray-300 focus:ring-[#8438ff]"
                />
                <span className="text-sm font-medium text-gray-700">Individual pages</span>
              </label>
            </div>

            {splitMode === 'ranges' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-[#8438ff]">Page Ranges</h4>
                  <button
                    onClick={addSplitRange}
                    className="btn btn-secondary"
                  >
                    Add Range
                  </button>
                </div>
                
                {splitRanges.length === 0 ? (
                  <p className="text-gray-600 text-center py-4">
                    No ranges defined. Click "Add Range" to get started.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {splitRanges.map((range) => (
                      <div key={range.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <input
                          type="text"
                          value={range.name}
                          onChange={(e) => updateSplitRange(range.id, 'name', e.target.value)}
                          placeholder="Range name"
                          className="flex-1 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                        />
                        
                        <span className="text-sm text-gray-600">Pages:</span>
                        
                        <input
                          type="number"
                          value={range.start}
                          onChange={(e) => updateSplitRange(range.id, 'start', parseInt(e.target.value) || 1)}
                          min="1"
                          max={pdfFile.pageCount}
                          className="w-20 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                        />
                        
                        <span className="text-sm text-gray-600">to</span>
                        
                        <input
                          type="number"
                          value={range.end}
                          onChange={(e) => updateSplitRange(range.id, 'end', parseInt(e.target.value) || 1)}
                          min="1"
                          max={pdfFile.pageCount}
                          className="w-20 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                        />
                        
                        <button
                          onClick={() => removeSplitRange(range.id)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {splitMode === 'pages' && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800">
                  This will split the PDF into {pdfFile.pageCount} individual files, one for each page.
                </p>
              </div>
            )}

            <div className="mt-6">
              <button
                onClick={splitPDF}
                disabled={isProcessing || (splitMode === 'ranges' && splitRanges.length === 0)}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Splitting...
                  </>
                ) : (
                  <>
                    <Scissors className="h-4 w-4 mr-2" />
                    Split PDF
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Split Results */}
        {splitResults.length > 0 && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#8438ff]">
                Split Results ({splitResults.length} files)
              </h3>
              <button
                onClick={downloadAll}
                className="btn btn-success"
              >
                <Download className="h-4 w-4 mr-2" />
                Download All
              </button>
            </div>
            
            <div className="space-y-3">
              {splitResults.map((result) => (
                <div key={result.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-6 w-6 text-gray-400" />
                    <div>
                      <p className="font-medium text-[#8438ff]">{result.name}</p>
                      <p className="text-sm text-gray-600">{result.pageCount} page(s)</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => downloadFile(result)}
                    className="btn btn-secondary"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfSplitter.footer}
          className="mt-12"
        />
      </div>
    </div>
  )
}

export default PDFSplitter
